/**
 * Color utility functions for automatic contrast calculation
 * Based on WCAG 2.1 guidelines for accessible color contrast
 */

/**
 * Parse HSL string "h s% l%" to numeric values
 */
export function parseHSL(hslString: string): { h: number; s: number; l: number } {
  const parts = hslString.trim().split(/\s+/);
  return {
    h: parseFloat(parts[0]) || 0,
    s: parseFloat(parts[1]) || 0,
    l: parseFloat(parts[2]) || 0,
  };
}

/**
 * Convert HSL to RGB values (0-1 range)
 */
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  s = s / 100;
  l = l / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }
  
  return {
    r: r + m,
    g: g + m,
    b: b + m,
  };
}

/**
 * Calculate relative luminance per WCAG 2.1
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function getRelativeLuminance(hslString: string): number {
  const { h, s, l } = parseHSL(hslString);
  const { r, g, b } = hslToRgb(h, s, l);
  
  // Apply gamma correction
  const gammaCorrect = (value: number) => {
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  
  const rLin = gammaCorrect(r);
  const gLin = gammaCorrect(g);
  const bLin = gammaCorrect(b);
  
  // Calculate luminance
  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

/**
 * Determine if a color is considered "light" or "dark"
 * Uses WCAG luminance threshold for optimal contrast
 */
export function isLightColor(hslString: string): boolean {
  const luminance = getRelativeLuminance(hslString);
  // Threshold based on WCAG contrast ratio calculations
  // Colors with luminance > 0.179 need dark text for 4.5:1 contrast
  return luminance > 0.179;
}

/**
 * Get contrasting foreground color (HSL string) for a given background
 * Returns white or dark text based on background luminance
 */
export function getContrastingForeground(backgroundHSL: string): string {
  const isLight = isLightColor(backgroundHSL);
  // Dark text for light backgrounds, white text for dark backgrounds
  return isLight ? '220 20% 12%' : '0 0% 100%';
}

/**
 * Get a muted version of the foreground color
 * Useful for secondary text on the same background
 */
export function getMutedForeground(backgroundHSL: string): string {
  const isLight = isLightColor(backgroundHSL);
  // Slightly less contrast for muted text
  return isLight ? '220 10% 40%' : '220 10% 70%';
}

/**
 * Calculate contrast ratio between two colors
 * Per WCAG 2.1: (L1 + 0.05) / (L2 + 0.05)
 */
export function getContrastRatio(foregroundHSL: string, backgroundHSL: string): number {
  const fgLuminance = getRelativeLuminance(foregroundHSL);
  const bgLuminance = getRelativeLuminance(backgroundHSL);
  
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Check if contrast meets WCAG AA standard (4.5:1 for normal text)
 */
export function meetsWCAGAA(foregroundHSL: string, backgroundHSL: string): boolean {
  return getContrastRatio(foregroundHSL, backgroundHSL) >= 4.5;
}

/**
 * Check if contrast meets WCAG AAA standard (7:1 for normal text)
 */
export function meetsWCAGAAA(foregroundHSL: string, backgroundHSL: string): boolean {
  return getContrastRatio(foregroundHSL, backgroundHSL) >= 7;
}
